import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Grid,
  Link,
  Container,
  Paper,
  CssBaseline,
  useTheme,
  Fade,
  Modal,
  Backdrop,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import { API_ENDPOINT, APP_BANNER } from "../../utils/constants";
import { isLoggedIn, saveToken } from "../../utils/util";
import { getDbHeader } from "../../utils/util";

const LoginPage = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const theme = useTheme();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    // Existing login logic remains unchanged
    try {
      const response = await fetch(`${API_ENDPOINT}user/login`, {
        method: "POST",
        ...getDbHeader(),
        body: JSON.stringify({ username, password }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Login failed");
      }

      const { token } = await response.json();
      saveToken(token);
      navigate("/home");
    } catch (error) {
      alert(error.message);
    } finally {
      setLoading(false);
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address");
      return;
    }
    setEmailError("");
    setResetLoading(true);

    try {
      const response = await fetch(
        `${API_ENDPOINT}org/send-pwd-reset-link-by-email`,
        {
          method: "POST",
          ...getDbHeader(),
          body: JSON.stringify({ email }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Password reset request failed");
      }

      alert("Password reset link has been sent to your email!");
      setForgotPasswordOpen(false);
      setEmail("");
    } catch (error) {
      alert(error.message);
    } finally {
      setResetLoading(false);
    }
  };

  useEffect(() => {
    if (isLoggedIn()) {
      navigate("/home");
    }
  }, []);

  return (
    <Container
      component="main"
      maxWidth="lg"
      sx={{
        minHeight: "100vh",
        py: 4,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <CssBaseline />
      <Grid
        container
        sx={{
          height: "100%",
          borderRadius: 4,
          overflow: "hidden",
          boxShadow: 3,
        }}
      >
        {/* Left Banner - Unchanged */}
        <Grid
          item
          xs={false}
          md={6}
          sx={{
            backgroundImage: `url(${APP_BANNER})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            position: "relative",
            "&:before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background:
                "linear-gradient(135deg, rgba(25,118,210,0.85) 0%, rgba(33,150,243,0.75) 100%)",
            },
          }}
        >
          <Box
            sx={{
              position: "relative",
              p: 8,
              color: "white",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              textAlign: "center",
            }}
          >
            <Fade in timeout={1000}>
              <Box>
                <Typography
                  variant="h2"
                  component="h1"
                  sx={
                    {
                      /* unchanged styles */
                    }
                  }
                >
                  BetaZen
                </Typography>
                <Typography
                  variant="h5"
                  sx={
                    {
                      /* unchanged styles */
                    }
                  }
                >
                  Admin Console
                </Typography>
                <Typography
                  variant="body1"
                  sx={
                    {
                      /* unchanged styles */
                    }
                  }
                >
                  Secure Access to Management Portal
                </Typography>
              </Box>
            </Fade>
          </Box>
        </Grid>

        {/* Right Login Form */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={24}
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: theme.palette.background.default,
              borderRadius: 0,
            }}
          >
            <Box sx={{ width: "100%", maxWidth: 450, px: 4, py: 6 }}>
              <Typography
                component="h1"
                variant="h3"
                sx={{
                  mb: 4,
                  fontWeight: 700,
                  color: theme.palette.primary.main,
                  textAlign: "center",
                }}
              >
                Welcome Back
              </Typography>

              <Box component="form" onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  margin="normal"
                  label="Username"
                  variant="outlined"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  autoFocus
                  sx={
                    {
                      /* unchanged styles */
                    }
                  }
                />

                <TextField
                  fullWidth
                  margin="normal"
                  label="Password"
                  type="password"
                  variant="outlined"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  sx={
                    {
                      /* unchanged styles */
                    }
                  }
                />

                <Box sx={{ textAlign: "right", mt: 1 }}>
                  <Link
                    href="#"
                    color="primary"
                    sx={{ fontWeight: 600 }}
                    onClick={(e) => {
                      e.preventDefault();
                      setForgotPasswordOpen(true);
                    }}
                  >
                    Forgot Password?
                  </Link>
                </Box>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  sx={
                    {
                      /* unchanged styles */
                    }
                  }
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={26} sx={{ color: "white" }} />
                  ) : (
                    "Sign In"
                  )}
                </Button>

                <Typography
                  variant="body2"
                  color="textSecondary"
                  align="center"
                  sx={{ mt: 3, opacity: 0.8 }}
                >
                  Need assistance?{" "}
                  <Link
                    href="mailto:info@betazen.in"
                    color="primary"
                    sx={{ fontWeight: 600 }}
                  >
                    Contact our team
                  </Link>
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Forgot Password Modal */}
      <Modal
        open={forgotPasswordOpen}
        onClose={() => setForgotPasswordOpen(false)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={forgotPasswordOpen}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 400,
              bgcolor: "background.paper",
              borderRadius: 2,
              boxShadow: 24,
              p: 4,
            }}
          >
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
            >
              <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                Reset Password
              </Typography>
              <IconButton
                onClick={() => setForgotPasswordOpen(false)}
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
              Enter your email address and we'll send you a link to reset your
              password.
            </Typography>

            <Box component="form" onSubmit={handleForgotPasswordSubmit}>
              <TextField
                fullWidth
                label="Email Address"
                variant="outlined"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                error={!!emailError}
                helperText={emailError}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  mt: 3,
                  py: 1,
                  borderRadius: 2,
                  fontWeight: 600,
                }}
                disabled={resetLoading}
              >
                {resetLoading ? (
                  <CircularProgress size={24} sx={{ color: "white" }} />
                ) : (
                  "Send Reset Link"
                )}
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>
    </Container>
  );
};

export default LoginPage;
